import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import { useDebounce } from '../../hooks/useDebounce.js'
import { statusColor, formatDate } from '../../utils/format.js'
import { listPatients } from '../../api/patients.js'
import {
  listScreeningCampaigns,
  getScreeningCampaign,
  getScreeningProtocol,
  listScreeningRecords,
  startScreeningEncounter,
  updateScreeningRecord,
  generateFitnessReport,
} from '../../api/screening.js'

/**
 * Doctor-facing routine medical screening.
 *
 * The /superuser/screening surface (admin-only) owns the things only a
 * platform admin may do: creating campaigns, editing protocols, deleting
 * records and reading the PHI-safe cohort report. This module is the
 * other half — what a *doctor* needs to actually run a screening:
 *
 *   * see the campaigns that exist,
 *   * screen a patient in (the intake — opens a clinical encounter),
 *   * jump into that encounter to record the clinical findings,
 *   * record the fitness-for-work outcome and pull the Fitness PDF.
 *
 * The screening API already allows all of this for a doctor
 * (`start_encounter` and the record PATCH are `IsDoctor | IsAdminRole`);
 * this file is purely the missing doctor UI. Nothing admin-only is
 * reachable here — no campaign create/edit, no protocol editor, no
 * cohort report.
 */

const FITNESS_OPTIONS = [
  ['', 'Not recorded'],
  ['fit', 'Fit for work'],
  ['fit_with_restrictions', 'Fit with restrictions'],
  ['unfit', 'Unfit for work'],
  ['refer', 'Refer for further assessment'],
]

// ===========================================================================
// Campaign list — rendered as the "Screening" tab on the doctor dashboard.
// ===========================================================================

/**
 * The list of screening campaigns a doctor can work on. Campaigns are
 * created by the platform admin; the doctor opens one to screen patients
 * into it.
 */
export function DoctorScreeningList() {
  const campaignsQ = useQuery({
    queryKey: ['doctor-screening-campaigns'],
    queryFn: () => listScreeningCampaigns(),
  })
  const campaigns = campaignsQ.data || []

  return (
    <Card>
      <CardHeader
        title="Routine medical screening"
        subtitle="Campaigns are set up by the platform admin. Open one to screen patients in, complete their encounter, and record the fitness-for-work outcome."
      />
      {campaignsQ.error && (
        <ErrorBanner error={campaignsQ.error} onRetry={() => campaignsQ.refetch()} />
      )}
      {campaignsQ.isLoading && (
        <div className="text-sm text-slate-400 py-4">Loading campaigns…</div>
      )}
      {campaignsQ.data && (
        campaigns.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-6 text-center">
            No screening campaigns yet. Campaigns are created by the platform
            admin — once one exists it will appear here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wide text-slate-500 border-b">
                <tr>
                  <th className="text-left p-2">Campaign</th>
                  <th className="text-left p-2">Organisation</th>
                  <th className="text-left p-2">Protocol</th>
                  <th className="text-left p-2 w-28">Status</th>
                  <th className="text-right p-2 w-24">Screened</th>
                  <th className="text-left p-2 w-28">Start</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {campaigns.map((c) => (
                  <tr key={c.id} className="hover:bg-sand-50/50">
                    <td className="p-2">
                      <Link
                        to={`/doctor/screening/campaigns/${c.id}`}
                        className="font-medium text-wantok-700 hover:underline"
                      >
                        {c.name}
                      </Link>
                    </td>
                    <td className="p-2 text-slate-600">{c.organization_name}</td>
                    <td className="p-2 text-slate-600">{c.protocol_name}</td>
                    <td className="p-2">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${statusColor(c.status)}`}>
                        {c.status_display || c.status}
                      </span>
                    </td>
                    <td className="p-2 text-right tabular-nums text-slate-700">
                      {c.screened_count}{c.expected_headcount ? ` / ${c.expected_headcount}` : ''}
                    </td>
                    <td className="p-2 text-slate-600">
                      {c.scheduled_start ? formatDate(c.scheduled_start) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </Card>
  )
}

// ===========================================================================
// Campaign detail — the doctor's working surface for one campaign.
// ===========================================================================

/**
 * /doctor/screening/campaigns/:id — one campaign, doctor view. Read-only
 * campaign header plus the people screened, with intake + fitness-outcome
 * controls. No cohort report and no campaign edit/delete — those are
 * admin-only and stay on /superuser/screening.
 */
export default function DoctorScreeningCampaignDetail() {
  const { id } = useParams()
  const campaignQ = useQuery({
    queryKey: ['doctor-screening-campaign', id],
    queryFn: () => getScreeningCampaign(id),
    enabled: Boolean(id),
  })
  const campaign = campaignQ.data

  return (
    <div className="space-y-4">
      <Link to="/doctor" className="text-xs text-wantok-700 hover:underline">
        ← Back to screening
      </Link>

      {campaignQ.error && (
        <ErrorBanner error={campaignQ.error} onRetry={() => campaignQ.refetch()} />
      )}
      {campaignQ.isLoading && (
        <Card><div className="text-sm text-slate-400">Loading campaign…</div></Card>
      )}

      {campaign && (
        <>
          <Card>
            <CardHeader
              title={campaign.name}
              subtitle={`${campaign.organization_name} · ${campaign.protocol_name}`}
              right={
                <span className={`inline-block rounded-full px-2.5 py-1 text-[11px] font-medium ${statusColor(campaign.status)}`}>
                  {campaign.status_display || campaign.status}
                </span>
              }
            />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-1">
              <Meta label="Delivery">{deliveryLabel(campaign.delivery_mode)}</Meta>
              <Meta label="Scheduled">
                {campaign.scheduled_start ? formatDate(campaign.scheduled_start) : '—'}
                {campaign.scheduled_end ? ` – ${formatDate(campaign.scheduled_end)}` : ''}
              </Meta>
              <Meta label="Expected">{campaign.expected_headcount || '—'}</Meta>
              <Meta label="Location">{campaign.location || '—'}</Meta>
            </div>
          </Card>

          <RecordsCard campaign={campaign} />
          <ProtocolChecklistCard
            protocolId={campaign.protocol}
            protocolName={campaign.protocol_name}
          />
        </>
      )}
    </div>
  )
}

// ===========================================================================
// People screened
// ===========================================================================

function RecordsCard({ campaign }) {
  const [showScreen, setShowScreen] = useState(false)
  const [fitnessFor, setFitnessFor] = useState(null)
  const recordsQ = useQuery({
    queryKey: ['doctor-screening-records', campaign.id],
    queryFn: () => listScreeningRecords({ campaign: campaign.id }),
    enabled: Boolean(campaign.id),
  })
  const records = recordsQ.data || []

  return (
    <Card>
      <CardHeader
        title="People screened"
        subtitle="Everyone screened into this campaign. Open an encounter to record the clinical findings."
        right={
          <button
            onClick={() => setShowScreen(true)}
            className="text-xs font-semibold tracking-wide px-3 py-1.5 rounded-full bg-wantok-600 text-white hover:bg-wantok-700 transition-colors"
          >
            + Screen a patient
          </button>
        }
      />
      {recordsQ.error && (
        <ErrorBanner error={recordsQ.error} onRetry={() => recordsQ.refetch()} />
      )}
      {recordsQ.isLoading && <div className="text-sm text-slate-400 py-3">Loading…</div>}
      {recordsQ.data && (
        records.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-6 text-center">
            No one screened yet. Use “+ Screen a patient” to start.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wide text-slate-500 border-b">
                <tr>
                  <th className="text-left p-2">Patient</th>
                  <th className="text-left p-2">Conducting doctor</th>
                  <th className="text-left p-2 w-24">Encounter</th>
                  <th className="text-left p-2 w-44">Fitness outcome</th>
                  <th className="p-2 w-56"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {records.map((rec) => (
                  <RecordRow key={rec.id} record={rec}
                    onFitness={() => setFitnessFor(rec)} />
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
      {showScreen && (
        <ScreenPatientModal campaign={campaign} onClose={() => setShowScreen(false)} />
      )}
      {fitnessFor && (
        <FitnessModal record={fitnessFor} onClose={() => setFitnessFor(null)} />
      )}
    </Card>
  )
}

function RecordRow({ record, onFitness }) {
  const genMut = useMutation({
    mutationFn: () => generateFitnessReport(record.encounter, {}),
  })
  const fitnessLabel =
    (FITNESS_OPTIONS.find(([v]) => v === record.fitness_outcome) || [])[1]
    || 'Not recorded'

  return (
    <tr className="hover:bg-sand-50/50">
      <td className="p-2 text-slate-800">{record.patient_name}</td>
      <td className="p-2 text-slate-600">{record.doctor_name || '—'}</td>
      <td className="p-2 text-slate-500 text-xs">{record.encounter_status}</td>
      <td className="p-2">
        <span className={record.fitness_outcome ? 'text-slate-700' : 'text-slate-400 italic'}>
          {fitnessLabel}
        </span>
      </td>
      <td className="p-2 text-right space-x-2 whitespace-nowrap">
        {/* The core loop: the screening encounter is an ordinary CNE
            encounter — opening it drops the doctor into the usual editor
            to record vitals, diagnoses and orders. */}
        <Link
          to={`/doctor/encounters/${record.encounter}`}
          className="text-xs font-semibold text-wantok-700 hover:underline"
        >
          Open encounter
        </Link>
        <button onClick={onFitness}
          className="text-xs text-wantok-700 hover:underline">Record fitness</button>
        <button onClick={() => genMut.mutate()} disabled={genMut.isPending}
          className="text-xs text-wantok-700 hover:underline disabled:opacity-50">
          {genMut.isPending ? 'Generating…' : 'Fitness PDF'}
        </button>
        {genMut.isSuccess && genMut.data?.file && (
          <a href={genMut.data.file} target="_blank" rel="noreferrer"
            className="text-xs text-emerald-700 hover:underline">Open</a>
        )}
        {genMut.isError && (
          <span className="text-xs text-rose-600" title={String(genMut.error?.message || '')}>
            failed
          </span>
        )}
      </td>
    </tr>
  )
}

// ===========================================================================
// Screen a patient (intake)
// ===========================================================================

/**
 * Intake modal. Unlike the admin surface — which picks from the
 * organisation's roster — the doctor screens whoever is in front of them,
 * so this searches all patients. (Doctors can't read org staff links, and
 * the intake endpoint only needs a patient id.) The conducting doctor is
 * the signed-in doctor: `start_encounter` defaults `doctor` to the caller
 * when no `doctor_id` is sent.
 */
function ScreenPatientModal({ campaign, onClose }) {
  const qc = useQueryClient()
  const [q, setQ] = useState('')
  const debouncedQ = useDebounce(q, 250)
  const [selected, setSelected] = useState(null)

  const canSearch = debouncedQ.trim().length >= 2
  const patientsQ = useQuery({
    queryKey: ['screening-patient-search', debouncedQ],
    queryFn: () => listPatients({ q: debouncedQ }),
    enabled: canSearch,
  })
  const results = patientsQ.data || []

  const mut = useMutation({
    mutationFn: () => startScreeningEncounter(campaign.id, { patient_id: selected.id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['doctor-screening-records', campaign.id] })
      qc.invalidateQueries({ queryKey: ['doctor-screening-campaign', String(campaign.id)] })
      qc.invalidateQueries({ queryKey: ['doctor-screening-campaigns'] })
      onClose()
    },
  })

  return (
    <Modal title="Screen a patient" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-slate-500">
          Find the patient in front of you. Screening opens a clinical
          encounter with you as the conducting doctor — complete it in the
          usual encounter editor.
        </p>
        <Field label="Search patients">
          <input
            type="search" value={q} autoFocus
            onChange={(e) => { setQ(e.target.value); setSelected(null) }}
            placeholder="Name, MRN or date of birth…"
            className={inputCls}
          />
        </Field>
        {!canSearch ? (
          <p className="text-xs text-slate-400 italic">
            Type at least 2 characters to search.
          </p>
        ) : patientsQ.isLoading ? (
          <p className="text-xs text-slate-400">Searching…</p>
        ) : patientsQ.error ? (
          <ErrorBanner error={patientsQ.error} onRetry={() => patientsQ.refetch()} />
        ) : results.length === 0 ? (
          <p className="text-xs text-slate-400 italic">
            No patients match “{debouncedQ}”.
          </p>
        ) : (
          <ul className="max-h-52 overflow-y-auto divide-y divide-slate-100 border border-sand-200 rounded-lg">
            {results.map((p) => {
              const isSel = selected?.id === p.id
              return (
                <li key={p.id}>
                  <button
                    type="button" onClick={() => setSelected(p)}
                    className={`w-full text-left px-3 py-2 ${isSel ? 'bg-wantok-50' : 'hover:bg-sand-50/60'}`}
                  >
                    <div className="text-sm font-medium text-slate-800">
                      {p.full_name || p.name || p.mrn || `Patient #${p.id}`}
                    </div>
                    <div className="text-xs text-slate-500 flex flex-wrap gap-x-3">
                      {p.mrn && <span className="font-mono">{p.mrn}</span>}
                      {p.sex && <span className="capitalize">{p.sex}</span>}
                      {p.province && <span>{p.province}</span>}
                    </div>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
        {mut.error && <FormError error={mut.error} />}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button" onClick={onClose} disabled={mut.isPending}
            className="px-3 py-1.5 rounded-md border border-sand-300 text-sm text-slate-600 hover:bg-sand-50"
          >
            Cancel
          </button>
          <button
            type="button" onClick={() => mut.mutate()}
            disabled={mut.isPending || !selected}
            className="px-3 py-1.5 rounded-md bg-wantok-600 text-white text-sm hover:bg-wantok-700 disabled:opacity-50"
          >
            {mut.isPending ? 'Screening…' : 'Screen patient'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

// ===========================================================================
// Fitness-for-work outcome
// ===========================================================================

function FitnessModal({ record, onClose }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    fitness_outcome: record.fitness_outcome || '',
    fitness_restrictions: record.fitness_restrictions || '',
    fitness_valid_until: record.fitness_valid_until || '',
  })
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }))

  const mut = useMutation({
    mutationFn: () => updateScreeningRecord(record.id, {
      fitness_outcome: form.fitness_outcome,
      fitness_restrictions: form.fitness_restrictions,
      fitness_valid_until: form.fitness_valid_until || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['doctor-screening-records', record.campaign] })
      onClose()
    },
  })

  return (
    <Modal title={`Fitness outcome — ${record.patient_name}`} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); mut.mutate() }} className="space-y-3">
        <Field label="Outcome">
          <select value={form.fitness_outcome}
            onChange={(e) => setF('fitness_outcome', e.target.value)}
            className={inputCls}>
            {FITNESS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
        <Field label="Restrictions / accommodations">
          <textarea rows={2} value={form.fitness_restrictions}
            onChange={(e) => setF('fitness_restrictions', e.target.value)}
            placeholder="Only when the outcome is fit with restrictions"
            className={inputCls} />
        </Field>
        <Field label="Valid until (optional)">
          <input type="date" value={form.fitness_valid_until}
            onChange={(e) => setF('fitness_valid_until', e.target.value)}
            className={inputCls} />
        </Field>
        <p className="text-[11px] text-slate-500">
          The Fitness for Work PDF pulls this outcome automatically.
        </p>
        {mut.error && <FormError error={mut.error} />}
        <ModalActions onClose={onClose} submitting={mut.isPending}
          submitLabel="Save outcome" />
      </form>
    </Modal>
  )
}

// ===========================================================================
// What to capture — the protocol checklist, read-only reference
// ===========================================================================

// The five categories a protocol item can carry, in the order they read
// best on the page. `category` is a closed set on the model; the label
// map falls through to the raw value if a new one is ever added.
const ITEM_CATEGORY_LABELS = {
  vital: 'Vital signs',
  measurement: 'Measurements',
  lab_panel: 'Lab panels / tests',
  imaging: 'Imaging studies',
  question: 'Questions / observations',
}
const ITEM_CATEGORY_ORDER = ['vital', 'measurement', 'lab_panel', 'imaging', 'question']

/**
 * Read-only reference panel: the checklist from the campaign's protocol,
 * so the doctor knows what the screening should cover. The actual
 * readings are still recorded in the encounter (vitals, diagnoses,
 * orders) — a protocol item is a prompt, not a data field.
 */
function ProtocolChecklistCard({ protocolId, protocolName }) {
  const q = useQuery({
    queryKey: ['doctor-screening-protocol', protocolId],
    queryFn: () => getScreeningProtocol(protocolId),
    enabled: Boolean(protocolId),
  })
  const protocol = q.data
  const items = protocol?.items || []

  // Categories present, in canonical order, then any unknown ones last.
  const presentCats = ITEM_CATEGORY_ORDER.filter(
    (c) => items.some((it) => it.category === c),
  )
  const extraCats = [...new Set(items.map((it) => it.category))].filter(
    (c) => !ITEM_CATEGORY_ORDER.includes(c),
  )
  const orderedCats = [...presentCats, ...extraCats]

  return (
    <Card>
      <CardHeader
        title="What to capture"
        subtitle={`Checklist from the ${protocolName || 'screening'} protocol. Record the findings in the encounter as you go.`}
      />
      {q.error && <ErrorBanner error={q.error} onRetry={() => q.refetch()} />}
      {q.isLoading && (
        <div className="text-sm text-slate-400 py-3">Loading checklist…</div>
      )}
      {protocol && (
        items.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-4 text-center">
            This protocol has no checklist items yet.
          </div>
        ) : (
          <div className="space-y-4">
            {protocol.description && (
              <p className="text-xs text-slate-500">{protocol.description}</p>
            )}
            {orderedCats.map((cat) => (
              <div key={cat}>
                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                  {ITEM_CATEGORY_LABELS[cat] || cat}
                </div>
                <ul className="space-y-1">
                  {items
                    .filter((it) => it.category === cat)
                    .map((it) => (
                      <li key={it.id} className="flex items-baseline gap-2 text-sm">
                        <span className="text-wantok-400" aria-hidden>•</span>
                        <span className="text-slate-700">{it.label}</span>
                        {!it.is_required && (
                          <span className="text-[10px] uppercase tracking-wide text-slate-400">
                            optional
                          </span>
                        )}
                        {it.guidance && (
                          <span className="text-xs text-slate-500">— {it.guidance}</span>
                        )}
                      </li>
                    ))}
                </ul>
              </div>
            ))}
          </div>
        )
      )}
    </Card>
  )
}

// ===========================================================================
// Shared building blocks
// ===========================================================================

const inputCls = 'w-full border border-sand-300 rounded-md px-2 py-1.5 text-sm bg-white'

function deliveryLabel(mode) {
  return ({ site_visit: 'Site visit', clinic: 'Clinic', telehealth: 'Telehealth' })[mode]
    || mode || '—'
}

function Meta({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-sm text-slate-700">{children}</div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  )
}

function FormError({ error }) {
  const msg = error?.response?.data?.error?.message
    || error?.response?.data?.detail
    || (error?.response?.data?.record && 'This person is already enrolled in the campaign.')
    || error?.message
    || 'Something went wrong.'
  return (
    <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
      {String(msg)}
    </div>
  )
}

function ModalActions({ onClose, submitting, submitLabel, disabled }) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button type="button" onClick={onClose} disabled={submitting}
        className="px-3 py-1.5 rounded-md border border-sand-300 text-sm text-slate-600 hover:bg-sand-50">
        Cancel
      </button>
      <button type="submit" disabled={submitting || disabled}
        className="px-3 py-1.5 rounded-md bg-wantok-600 text-white text-sm hover:bg-wantok-700 disabled:opacity-50">
        {submitting ? 'Saving…' : submitLabel}
      </button>
    </div>
  )
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-slate-900/40 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-xl border border-sand-200 w-full max-w-lg mt-12 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-semibold text-slate-900">{title}</h2>
          <button onClick={onClose}
            className="text-slate-400 hover:text-slate-700 text-xl leading-none"
            aria-label="Close">×</button>
        </div>
        {children}
      </div>
    </div>
  )
}
